/**
 * <s-sticky-atc> — Mobile add-to-cart bar that appears once the main Add to Cart
 * button has scrolled out of view, and hides again near the footer.
 *
 * The bar lives inside the product-form partial, so it re-renders with the selected
 * variant (price, availability) and reconnects after every variant change. Its submit
 * button targets the main product form by id, so s-product-form handles the add.
 *
 * Required markup:
 *   [data-main-atc] (anywhere in the document) — the main Add to Cart button to watch.
 *   footer                                     — optional; the bar hides while it is visible.
 *
 * State:
 *   data-visible (on self) — present while the bar should show. Styling keys off it.
 */
class SStickyAtc extends HTMLElement {
  #observer;
  #mainVisible = true;
  #footerVisible = false;

  connectedCallback() {
    const main = document.querySelector('[data-main-atc]');
    const footer = document.querySelector('footer');
    if (!main) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === main) {
          // Only count it as "gone" once it has scrolled above the viewport, not before reaching it.
          this.#mainVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
        } else {
          this.#footerVisible = entry.isIntersecting;
        }
      }
      const show = !this.#mainVisible && !this.#footerVisible;
      this.toggleAttribute('data-visible', show);
      this.toggleAttribute('inert', !show);
    });

    this.#observer.observe(main);
    if (footer) this.#observer.observe(footer);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('s-sticky-atc')) {
  customElements.define('s-sticky-atc', SStickyAtc);
}
